/**
 * Proposal Blocks — typed block-model document engine (PURE).
 *
 * Directly fixes three auditor findings:
 *   1. "Monolithic text blob" — a proposal is now an ordered list of
 *      typed blocks (`ProposalBlock` discriminated union on `kind`)
 *      plus a `BrandKit`, not one markdown string.
 *   2. "Pricing is not interactive" — `PricingConfig` +
 *      `applyBuyerSelection` + `computeTotals` give buyers governed
 *      knobs (toggle optionals, edit quantities within min/max) with
 *      instant deterministic recompute.
 *   3. "No immutable versioning" — `snapshotVersion` deep-freezes a
 *      structural clone and stamps it with a deterministic content
 *      hash (internal stableStringify + FNV-1a), so every sent version
 *      is provably what the buyer saw.
 *
 * Complements (does not replace) proposalDoc.ts (HTML print renderer)
 * and quoteBlock.ts (tiered quote engine): this module owns the typed
 * block union, buyer-side selection over a flat `PricingConfig`, and
 * hashed version snapshots. Zero imports from the store, auth, or
 * firebase — everything here is deterministic and trivially testable.
 * No customer data is ever fabricated: merge tokens resolve only from
 * the caller-supplied context, and unresolved tokens are FLAGGED (kept
 * visible + listed) rather than silently blanked.
 */

// ---------------------------------------------------------------------------
// Block model — discriminated union on `kind`
// ---------------------------------------------------------------------------

/** Free-form rich-text block (markdown-ish string, merge tokens allowed). */
export interface TextBlock {
  kind: 'text';
  id: string;
  text: string;
  /** Tokens that failed to resolve in the last mergeContext pass. */
  unresolvedTokens?: string[];
}

/** Cover / section hero: big headline + optional subheadline. */
export interface HeroBlock {
  kind: 'hero';
  id: string;
  headline: string;
  subheadline?: string;
  /** Tokens that failed to resolve in the last mergeContext pass. */
  unresolvedTokens?: string[];
}

/** Full-width image (brand asset, architecture diagram, roadmap…). */
export interface ImageBlock {
  kind: 'image';
  id: string;
  url: string;
  alt?: string;
  caption?: string;
}

/**
 * Interactive pricing block — a placeholder the renderer fills with the
 * live pricing table driven by a `PricingConfig` (kept separate so the
 * document structure and the buyer-editable numbers version cleanly).
 */
export interface QuoteBlock {
  kind: 'quote';
  id: string;
  heading?: string;
}

/** Social proof: a customer quote with attribution. */
export interface TestimonialBlock {
  kind: 'testimonial';
  id: string;
  quote: string;
  author: string;
  role?: string;
}

export type ProposalBlock =
  | TextBlock
  | HeroBlock
  | ImageBlock
  | QuoteBlock
  | TestimonialBlock;

/** Brand identity applied across every block of the document. */
export interface BrandKit {
  logoUrl: string;
  primary: string;
  accent: string;
  fontFamily: string;
}

/** The whole block-model document. */
export interface ProposalDocModel {
  id: string;
  blocks: ProposalBlock[];
  brandKit: BrandKit;
  /** Monotonic snapshot counter — bumped by snapshotVersion, never by edits. */
  versionSeq: number;
}

// ---------------------------------------------------------------------------
// Pricing model — buyer-configurable
// ---------------------------------------------------------------------------

export interface PricingLineItem {
  id: string;
  label: string;
  unitPrice: number;
  qty: number;
  /** Buyer may check/uncheck this line; required lines always count. */
  optional: boolean;
  selected: boolean;
  minQty?: number;
  maxQty?: number;
  /** Billing cadence — null/undefined = one-time. */
  recurring?: 'monthly' | 'annual' | null;
}

export interface PricingConfig {
  lineItems: PricingLineItem[];
  /** ISO currency code, e.g. 'USD'. */
  currency: string;
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

const round2 = (n: number): number => Math.round(n * 100) / 100;

const TOKEN_RE = /\{\{\s*([\w.]+)\s*\}\}/g;

/**
 * Deterministic JSON with sorted object keys — key insertion order can
 * never change the hash. Handles primitives, arrays, plain objects.
 */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) {
    return '[' + value.map((v) => stableStringify(v)).join(',') + ']';
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj)
    .filter((k) => obj[k] !== undefined)
    .sort();
  return (
    '{' +
    keys.map((k) => JSON.stringify(k) + ':' + stableStringify(obj[k])).join(',') +
    '}'
  );
}

/** FNV-1a 32-bit — small, deterministic, dependency-free content hash. */
function fnv1a(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/** Structural clone via JSON — the model is serializable by design. */
function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const key of Object.keys(value as object)) {
      deepFreeze((value as Record<string, unknown>)[key]);
    }
  }
  return value;
}

/** Content hash covers the substance (blocks + brand), not versionSeq. */
function contentHash(doc: ProposalDocModel): string {
  return fnv1a(stableStringify({ blocks: doc.blocks, brandKit: doc.brandKit }));
}

// ---------------------------------------------------------------------------
// createProposalDoc
// ---------------------------------------------------------------------------

/**
 * Build a fresh document from ordered blocks + a brand kit. Pure and
 * deterministic: the id derives from the initial content hash so the
 * same input always yields the same document. versionSeq starts at 0 —
 * only snapshotVersion advances it.
 */
export function createProposalDoc(input: {
  blocks: ProposalBlock[];
  brandKit: BrandKit;
}): ProposalDocModel {
  const blocks = clone(input.blocks);
  const brandKit = clone(input.brandKit);
  const id =
    'pdoc-' + fnv1a(stableStringify({ blocks, brandKit }));
  return { id, blocks, brandKit, versionSeq: 0 };
}

// ---------------------------------------------------------------------------
// mergeContext — token resolution that flags, never blanks
// ---------------------------------------------------------------------------

/**
 * Resolve `{{tokens}}` in text and hero blocks from the caller-supplied
 * context. A token missing from the context is left VISIBLE in the text
 * (never silently blanked — the classic mail-merge embarrassment) and
 * reported on the block's `unresolvedTokens` list so the send path can
 * gate on it. Pure: returns a new document, input untouched.
 */
export function mergeContext(
  doc: ProposalDocModel,
  ctx: Record<string, string | number>,
): ProposalDocModel {
  const resolveText = (
    text: string,
    flagged: Set<string>,
  ): string =>
    text.replace(TOKEN_RE, (whole, token: string) => {
      const value = ctx[token];
      if (value === undefined) {
        flagged.add(token);
        return whole; // keep the token visible — flagged, not blanked
      }
      return String(value);
    });

  const blocks = doc.blocks.map((block): ProposalBlock => {
    if (block.kind === 'text') {
      const flagged = new Set<string>();
      const text = resolveText(block.text, flagged);
      return { ...block, text, unresolvedTokens: [...flagged] };
    }
    if (block.kind === 'hero') {
      const flagged = new Set<string>();
      const headline = resolveText(block.headline, flagged);
      const subheadline =
        block.subheadline === undefined
          ? undefined
          : resolveText(block.subheadline, flagged);
      return { ...block, headline, subheadline, unresolvedTokens: [...flagged] };
    }
    return { ...block };
  });

  return { ...doc, blocks };
}

// ---------------------------------------------------------------------------
// applyBuyerSelection — governed buyer edits (immutable)
// ---------------------------------------------------------------------------

/**
 * Apply one buyer edit to the pricing config. Rules:
 *   • qty edits clamp to [minQty, maxQty] (and never below 0);
 *   • `selected` toggles apply to OPTIONAL items only — attempts to
 *     deselect a required line are ignored (required lines always count);
 *   • unknown line ids are a no-op.
 * Pure: the input config is never mutated.
 */
export function applyBuyerSelection(
  cfg: PricingConfig,
  edit: { lineItemId: string; selected?: boolean; qty?: number },
): PricingConfig {
  return {
    ...cfg,
    lineItems: cfg.lineItems.map((item) => {
      if (item.id !== edit.lineItemId) return { ...item };
      let next: PricingLineItem = { ...item };
      if (edit.qty !== undefined) {
        const raw = Number.isFinite(edit.qty) ? edit.qty : 0;
        const lo = item.minQty ?? 0;
        const hi = item.maxQty ?? Number.POSITIVE_INFINITY;
        next = { ...next, qty: Math.min(hi, Math.max(lo, Math.max(0, raw))) };
      }
      if (edit.selected !== undefined && item.optional) {
        next = { ...next, selected: edit.selected };
      }
      return next;
    }),
  };
}

// ---------------------------------------------------------------------------
// computeTotals — instant deterministic recompute
// ---------------------------------------------------------------------------

/**
 * Totals for the current selection. Required lines always count;
 * optional lines count only while selected. Cadences are kept separate
 * (per-period face value) so the UI can show them honestly; `subtotal`
 * is the sum of all counted lines at face value.
 */
export function computeTotals(cfg: PricingConfig): {
  subtotal: number;
  recurringMonthly: number;
  recurringAnnual: number;
  oneTime: number;
} {
  let oneTime = 0;
  let recurringMonthly = 0;
  let recurringAnnual = 0;
  for (const item of cfg.lineItems) {
    if (item.optional && !item.selected) continue;
    const line = round2(Math.max(0, item.qty) * item.unitPrice);
    if (item.recurring === 'monthly') recurringMonthly += line;
    else if (item.recurring === 'annual') recurringAnnual += line;
    else oneTime += line;
  }
  return {
    subtotal: round2(oneTime + recurringMonthly + recurringAnnual),
    recurringMonthly: round2(recurringMonthly),
    recurringAnnual: round2(recurringAnnual),
    oneTime: round2(oneTime),
  };
}

// ---------------------------------------------------------------------------
// snapshotVersion — immutable, hashed, monotonic
// ---------------------------------------------------------------------------

/**
 * Take an immutable snapshot of the document: a deep-frozen structural
 * clone with the version sequence advanced by one and a deterministic
 * content hash (stable-stringified blocks + brand kit through FNV-1a).
 * Identical content always hashes identically; any block edit changes
 * the hash. The input document is never mutated.
 */
export function snapshotVersion(doc: ProposalDocModel): {
  version: number;
  hash: string;
  frozen: ProposalDocModel;
} {
  const version = doc.versionSeq + 1;
  const frozen = deepFreeze({ ...clone(doc), versionSeq: version });
  return { version, hash: contentHash(doc), frozen };
}

// ---------------------------------------------------------------------------
// assertEditable — "editing after view/sign blocked"
// ---------------------------------------------------------------------------

/**
 * Structural edits are allowed ONLY while the document is a draft.
 * Once sent (and certainly once viewed or signed) the content the buyer
 * saw must stay exactly what the version hash attests to — changes
 * require a new draft + a new snapshot.
 */
export function assertEditable(
  doc: ProposalDocModel,
  status: 'draft' | 'sent' | 'viewed' | 'signed',
): void {
  if (status !== 'draft') {
    throw new Error(
      `Proposal ${doc.id} is '${status}' — only draft documents are editable. ` +
        'Create a new version instead of editing what the buyer already saw.',
    );
  }
}

// ---------------------------------------------------------------------------
// renderPublicViewModel — recipient-safe composition
// ---------------------------------------------------------------------------

/**
 * The recipient-facing shape: blocks + brand + live totals + whether
 * the pricing is interactive (any buyer-toggleable optional line).
 * Composes with publicProposalViewModel's philosophy — pure, no store,
 * nothing internal leaks — without importing it.
 */
export function renderPublicViewModel(
  doc: ProposalDocModel,
  cfg: PricingConfig,
): {
  blocks: ProposalBlock[];
  brandKit: BrandKit;
  totals: ReturnType<typeof computeTotals>;
  interactive: boolean;
} {
  return {
    blocks: doc.blocks.map((b) => ({ ...b })),
    brandKit: { ...doc.brandKit },
    totals: computeTotals(cfg),
    interactive: cfg.lineItems.some((i) => i.optional),
  };
}

// ---------------------------------------------------------------------------
// ProposalBlockCanvas — pure serializable render tree
// ---------------------------------------------------------------------------

/** One node of the serializable render tree (no JSX, no store). */
export interface CanvasNode {
  type: string;
  props: Record<string, string | number | boolean>;
  children: Array<CanvasNode | string>;
}

const prune = (
  props: Record<string, string | number | boolean | undefined>,
): Record<string, string | number | boolean> => {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(props)) if (v !== undefined) out[k] = v;
  return out;
};

/**
 * Render helper: turn a document + pricing config into a serializable
 * render tree the UI (or any renderer — HTML, PDF, email) can walk.
 * One child node per block, in order; the quote block carries the live
 * totals + line rows so a pricing table can render without recomputing.
 */
export function ProposalBlockCanvas(
  doc: ProposalDocModel,
  cfg: PricingConfig,
): CanvasNode {
  const totals = computeTotals(cfg);
  const interactive = cfg.lineItems.some((i) => i.optional);

  const children: CanvasNode[] = doc.blocks.map((block): CanvasNode => {
    switch (block.kind) {
      case 'hero':
        return {
          type: 'hero-block',
          props: prune({
            id: block.id,
            headline: block.headline,
            subheadline: block.subheadline,
            accent: doc.brandKit.accent,
          }),
          children: [],
        };
      case 'text':
        return {
          type: 'text-block',
          props: prune({ id: block.id }),
          children: [block.text],
        };
      case 'image':
        return {
          type: 'image-block',
          props: prune({
            id: block.id,
            url: block.url,
            alt: block.alt,
            caption: block.caption,
          }),
          children: [],
        };
      case 'quote':
        return {
          type: 'quote-block',
          props: prune({
            id: block.id,
            heading: block.heading,
            currency: cfg.currency,
            subtotal: totals.subtotal,
            oneTime: totals.oneTime,
            recurringMonthly: totals.recurringMonthly,
            recurringAnnual: totals.recurringAnnual,
            interactive,
          }),
          children: cfg.lineItems.map((item) => ({
            type: 'pricing-line',
            props: prune({
              id: item.id,
              label: item.label,
              unitPrice: item.unitPrice,
              qty: item.qty,
              optional: item.optional,
              selected: item.optional ? item.selected : true,
              recurring: item.recurring ?? 'once',
            }),
            children: [],
          })),
        };
      case 'testimonial':
        return {
          type: 'testimonial-block',
          props: prune({ id: block.id, author: block.author, role: block.role }),
          children: [block.quote],
        };
    }
  });

  return {
    type: 'canvas',
    props: prune({
      docId: doc.id,
      versionSeq: doc.versionSeq,
      fontFamily: doc.brandKit.fontFamily,
      primary: doc.brandKit.primary,
      accent: doc.brandKit.accent,
      logoUrl: doc.brandKit.logoUrl,
    }),
    children,
  };
}
